import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  FormControlLabel,
  Paper,
  Select,
  MenuItem,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import type { Game } from './engine/Game';
import type { CarPreset, CarTuning } from './game/cars/CarTypes';
import { CAR_CATALOG } from './game/cars/CarCatalog';
import OpenWorldDriveHUD from './components/OpenWorldDriveHUD';
import RaceSetupMenu from './ui/RaceSetupMenu';
import { VirtualJoystick, type JoystickState } from './ui/VirtualJoystick';
import { TouchButton } from './ui/TouchButton';
import type { PlayerStatus } from './engine/OpenWorldManager';

type UiState = ReturnType<Game['getUiState']>;

type SelectedCar = {
  id: string;
  name: string;
  accel: number;
  brake: number;
  maxSpeed: number;
  handling: number;
  grip: number;
  driftGrip: number;
  drag: number;
};

type SliderRange = { min: number; max: number; step: number };

type SliderConfig = {
  accel: SliderRange;
  brake: SliderRange;
  maxSpeed: SliderRange;
  handling: SliderRange;
  grip: SliderRange;
  driftGrip: SliderRange;
  drag: SliderRange;
};

type TouchHandlers = {
  onPointerDown: () => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onPointerCancel: () => void;
};


export type CarRaceGameViewProps = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  mountRef: React.RefObject<HTMLDivElement | null>;
  minimapRef: React.RefObject<HTMLCanvasElement | null>;
  isFullscreen: boolean;
  fps: number;
  selectedCar: SelectedCar;
  activeView: 'menu' | 'garage' | 'race' | 'free';
  onModeSwitch: (event: React.MouseEvent<HTMLElement>, next: 'menu' | 'garage' | 'race' | 'free' | null) => void;
  onOpenRaceSetup: () => void;
  onStartFreeRide: () => void;
  onToggleFullscreen: () => void;
  status: UiState | null;
  paused: boolean;
  ghostEnabled: boolean;
  onPauseToggle: () => void;
  onReset: () => void;
  onRaceStart: () => void;
  onGhostToggle: () => void;
  onToggleGarage: () => void;
  garageOpen: boolean;
  sliderConfig: SliderConfig;
  currentTuning: CarTuning;
  onSliderChange: (key: keyof CarTuning, value: number) => void;
  colorValue: string;
  neonColorValue: string;
  neonIntensityValue: number;
  onApplyTuning: (next: CarTuning) => void;
  presetName: string;
  onPresetNameChange: (value: string) => void;
  onSavePreset: () => void;
  presetsByCarId: Record<string, CarPreset[]>;
  onApplyPreset: (preset: CarPreset) => void;
  onDeletePreset: (presetId: string) => void;
  onResetTuning: () => void;
  onSaveTuning: () => void;
  saving: boolean;
  saveMessage: string | null;
  createTouchHandlers: (keyCode: string) => TouchHandlers;
  raceSetupOpen: boolean;
  onCloseRaceSetup: () => void;
  onStartRaceSetup: (carId: string, trackId: string) => void;
  onOpenGarageFromSetup: () => void;
  isMobile?: boolean;
  onJoystickChange?: (state: JoystickState) => void;
  freeRideMapId: string;
  onFreeRideMapChange: (value: string) => void;
  touchControlsEnabled: boolean;
  onToggleTouchControls: () => void;
};

const CarRaceGameView: React.FC<CarRaceGameViewProps> = ({
  containerRef,
  mountRef,
  minimapRef,
  isFullscreen,
  fps,
  selectedCar,
  activeView,
  onModeSwitch,
  onOpenRaceSetup,
  onStartFreeRide,
  onToggleFullscreen,
  status,
  paused,
  ghostEnabled,
  onPauseToggle,
  onReset,
  onRaceStart,
  onGhostToggle,
  onToggleGarage,
  garageOpen,
  sliderConfig,
  currentTuning,
  onSliderChange,
  colorValue,
  neonColorValue,
  neonIntensityValue,
  onApplyTuning,
  presetName,
  onPresetNameChange,
  onSavePreset,
  presetsByCarId,
  onApplyPreset,
  onDeletePreset,
  onResetTuning,
  onSaveTuning,
  saving,
  saveMessage,
  createTouchHandlers,
  raceSetupOpen,
  onCloseRaceSetup,
  onStartRaceSetup,
  onOpenGarageFromSetup,
  isMobile = false,
  onJoystickChange,
  freeRideMapId,
  onFreeRideMapChange,
  touchControlsEnabled,
  onToggleTouchControls
}) => {
  const raceHudStatus: PlayerStatus | null = status ? {
    position: status.position,
    speed: status.speed,
    onRoad: true,
    currentRoadType: undefined,
    speedLimit: undefined,
    isOverspeed: false,
    wantedLevel: 0,
    gpsLocation: {
      lat: 0,
      lon: 0,
      cityName: status.carName ?? null,
      roadName: 'Race Track'
    }
  } : null;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: isFullscreen ? '100%' : 'min(1480px, calc(100vw - 32px))',
        maxWidth: '100%',
        mx: 'auto',
        height: isFullscreen ? '100vh' : 'auto',
        ...(isFullscreen && { position: 'fixed', top: 0, left: 0, zIndex: 9999, backgroundColor: '#000' })
      }}
    >
      <Stack spacing={isFullscreen ? 0 : 2} sx={{ flex: 1, width: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: isFullscreen ? 1 : 0 }}>
          <Typography variant={isFullscreen ? 'body2' : 'h6'} sx={{ color: isFullscreen ? '#fff' : 'inherit' }}>
            Auto-Rennspiel (Three.js)
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ color: isFullscreen ? '#aaa' : 'inherit' }}>
              FPS: {fps}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ color: isFullscreen ? '#aaa' : 'inherit' }}>
              Car: {selectedCar.name}
            </Typography>
            <Button
              size="small"
              variant={touchControlsEnabled ? "contained" : "outlined"}
              color={touchControlsEnabled ? "primary" : "inherit"}
              onClick={onToggleTouchControls}
              sx={{ 
                minWidth: 140,
                fontWeight: 600,
                ...(isFullscreen && { color: touchControlsEnabled ? 'inherit' : '#fff', borderColor: '#fff' })
              }}
            >
              🎮 Touch: {touchControlsEnabled ? "AN" : "AUS"}
            </Button>
            <Select
              size="small"
              value={freeRideMapId}
              onChange={(event) => onFreeRideMapChange(String(event.target.value))}
              sx={{ minWidth: 190 }}
              disabled={activeView === 'race'}
            >
              <MenuItem value="bw_openworld">Freie Fahrt: BW</MenuItem>
              <MenuItem value="moscow_racing">Freie Fahrt: Moscow</MenuItem>
              <MenuItem value="nfs_u2_city">Freie Fahrt: NFS U2 City</MenuItem>
            </Select>
            <Tooltip title={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}>
              <IconButton onClick={onToggleFullscreen} size="small" sx={{ color: isFullscreen ? '#fff' : 'inherit' }}>
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {!isFullscreen && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
              <ToggleButtonGroup
                value={activeView}
                exclusive
                size="small"
                onChange={onModeSwitch}
                aria-label="Car Race Navigation"
              >
                <ToggleButton value="menu">Menu</ToggleButton>
                <ToggleButton value="race">Rennen</ToggleButton>
                <ToggleButton value="free">Freie Fahrt</ToggleButton>
                <ToggleButton value="garage">Garage</ToggleButton>
              </ToggleButtonGroup>

              {activeView === 'menu' && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined" label="Main menu" />
                  <Chip size="small" variant="outlined" label={`Auto: ${selectedCar.name}`} />
                  <Button size="small" variant="contained" onClick={onOpenRaceSetup}>
                    Auto & Strecke waehlen
                  </Button>
                  <Button size="small" variant="outlined" onClick={onStartFreeRide}>
                    Freie Fahrt starten
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        )}

        <Paper
          variant="outlined"
          sx={{
            p: isFullscreen ? 0 : 1,
            flex: 1,
            borderRadius: isFullscreen ? 0 : 1,
            overflow: 'hidden',
            backgroundColor: 'background.default',
            border: isFullscreen ? 'none' : 'default'
          }}
        >
          <Box
            ref={mountRef}
            sx={{
              position: 'relative',
              width: '100%',
              height: isFullscreen ? '100%' : { xs: 360, md: 520 },
              borderRadius: isFullscreen ? 0 : 1,
              overflow: 'hidden',
              backgroundColor: 'background.default'
            }}
          >
            {(activeView === 'race' || activeView === 'free') && status?.openWorld?.isOpenWorld && (
              <OpenWorldDriveHUD
                playerStatus={status.openWorld.playerStatus}
                weather={status.openWorld.weather}
                timeOfDay={status.openWorld.timeOfDay}
                headingRad={status.heading}
              />
            )}

            {(activeView === 'race' || activeView === 'free') && !status?.openWorld?.isOpenWorld && raceHudStatus && (
              <OpenWorldDriveHUD
                playerStatus={raceHudStatus}
                weather={null}
                timeOfDay={null}
                headingRad={status?.heading ?? 0}
              />
            )}
          </Box>
        </Paper>

        {!isFullscreen && (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button variant="contained" color={paused ? 'primary' : 'warning'} onClick={onPauseToggle}>
                  {paused ? 'Fortsetzen' : 'Pause'}
                </Button>
                <Button variant="outlined" onClick={onReset}>Reset</Button>
                <Button variant="outlined" onClick={onRaceStart}>Race Start (T)</Button>
                <Button variant={ghostEnabled ? 'outlined' : 'text'} onClick={onGhostToggle}>
                  Ghost {ghostEnabled ? 'An' : 'Aus'}
                </Button>
                <Button variant="text" onClick={onToggleGarage}>
                  Garage (G)
                </Button>
              </Stack>

              {activeView === 'menu' && (
                <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                  <Stack spacing={0.5} alignItems="flex-end">
                    <Typography variant="body2" color="text.secondary">
                      Lap {status?.currentLap || 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Race: {status?.raceActive ? `${status.raceTime.toFixed(2)}s` : 'Idle'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Best: {status?.bestTime ? `${status.bestTime.toFixed(2)}s` : '--'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Splits: {(status?.splits || []).map((split) => split.toFixed(2)).join(' | ') || '--'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(status?.laps || []).length > 0 && `Laps: ${(status?.laps || []).length}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ghost: {status?.ghostAvailable ? (ghostEnabled ? 'Aktiv' : 'Aus') : 'Keine Daten'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Minimap</Typography>
                    <Box sx={{ width: 140, height: 140, borderRadius: 1, overflow: 'hidden' }}>
                      <Box
                        component="canvas"
                        ref={minimapRef}
                        width={140}
                        height={140}
                        sx={{ display: 'block' }}
                      />
                    </Box>
                  </Stack>
                </Stack>
              )}
            </Stack>

            {Boolean(status?.openWorld?.isOpenWorld) && null}

            {garageOpen && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Garage & Tuning: {selectedCar.name}
                  </Typography>

                  <Stack spacing={1}>
                    <Typography variant="caption">Accel</Typography>
                    <Slider
                      value={currentTuning.accel ?? selectedCar.accel}
                      min={sliderConfig.accel.min}
                      max={sliderConfig.accel.max}
                      step={sliderConfig.accel.step}
                      onChange={(_, value) => onSliderChange('accel', Number(value))}
                    />

                    <Typography variant="caption">Brake</Typography>
                    <Slider
                      value={currentTuning.brake ?? selectedCar.brake}
                      min={sliderConfig.brake.min}
                      max={sliderConfig.brake.max}
                      step={sliderConfig.brake.step}
                      onChange={(_, value) => onSliderChange('brake', Number(value))}
                    />

                    <Typography variant="caption">Max Speed</Typography>
                    <Slider
                      value={currentTuning.maxSpeed ?? selectedCar.maxSpeed}
                      min={sliderConfig.maxSpeed.min}
                      max={sliderConfig.maxSpeed.max}
                      step={sliderConfig.maxSpeed.step}
                      onChange={(_, value) => onSliderChange('maxSpeed', Number(value))}
                    />

                    <Typography variant="caption">Handling</Typography>
                    <Slider
                      value={currentTuning.handling ?? selectedCar.handling}
                      min={sliderConfig.handling.min}
                      max={sliderConfig.handling.max}
                      step={sliderConfig.handling.step}
                      onChange={(_, value) => onSliderChange('handling', Number(value))}
                    />

                    <Typography variant="caption">Grip</Typography>
                    <Slider
                      value={currentTuning.grip ?? selectedCar.grip}
                      min={sliderConfig.grip.min}
                      max={sliderConfig.grip.max}
                      step={sliderConfig.grip.step}
                      onChange={(_, value) => onSliderChange('grip', Number(value))}
                    />

                    <Typography variant="caption">Drift Grip</Typography>
                    <Slider
                      value={currentTuning.driftGrip ?? selectedCar.driftGrip}
                      min={sliderConfig.driftGrip.min}
                      max={sliderConfig.driftGrip.max}
                      step={sliderConfig.driftGrip.step}
                      onChange={(_, value) => onSliderChange('driftGrip', Number(value))}
                    />

                    <Typography variant="caption">Drag</Typography>
                    <Slider
                      value={currentTuning.drag ?? selectedCar.drag}
                      min={sliderConfig.drag.min}
                      max={sliderConfig.drag.max}
                      step={sliderConfig.drag.step}
                      onChange={(_, value) => onSliderChange('drag', Number(value))}
                    />

                    <Typography variant="caption">Body Color</Typography>
                    <Box
                      component="input"
                      type="color"
                      title="Body Color"
                      aria-label="Body Color"
                      value={colorValue}
                      onChange={(event) => onApplyTuning({ ...currentTuning, colorHex: event.target.value })}
                      sx={{ width: '100%', height: 36, border: 'none', background: 'transparent' }}
                    />

                    <Typography variant="caption">Neon Color</Typography>
                    <Box
                      component="input"
                      type="color"
                      title="Neon Color"
                      aria-label="Neon Color"
                      value={neonColorValue}
                      onChange={(event) => onApplyTuning({ ...currentTuning, neonColorHex: event.target.value })}
                      sx={{ width: '100%', height: 36, border: 'none', background: 'transparent' }}
                    />

                    <Typography variant="caption">Neon Intensity</Typography>
                    <Slider
                      value={neonIntensityValue}
                      min={0}
                      max={0.6}
                      step={0.02}
                      onChange={(_, value) => onSliderChange('neonIntensity', Number(value))}
                    />
                  </Stack>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Presets</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <TextField
                        size="small"
                        label="Preset Name"
                        value={presetName}
                        onChange={(event) => onPresetNameChange(event.target.value)}
                        sx={{ flex: 1, minWidth: 200 }}
                      />
                      <Button variant="outlined" onClick={onSavePreset}>
                        Preset speichern
                      </Button>
                    </Stack>

                    {(presetsByCarId[selectedCar.id] || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Noch keine Presets gespeichert.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {(presetsByCarId[selectedCar.id] || []).map((preset) => (
                          <Stack key={preset.id} direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {preset.name}
                            </Typography>
                            <Button variant="text" onClick={() => onApplyPreset(preset)}>
                              Anwenden
                            </Button>
                            <Button variant="text" color="error" onClick={() => onDeletePreset(preset.id)}>
                              Loeschen
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="text" onClick={onResetTuning}>Reset to Stock</Button>
                    <Button variant="contained" onClick={onSaveTuning} disabled={saving}>
                      {saving ? 'Speichere...' : 'Speichern'}
                    </Button>
                  </Stack>
                  {saveMessage && (
                    <Typography variant="caption" color="text.secondary">
                      {saveMessage}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1} alignItems="center">
                <Typography variant="subtitle2">Touch-Steuerung</Typography>
                <Typography variant="caption" color="text.secondary">
                  Joystick + Gas/Bremse/Drift sind oben aktivierbar/deaktivierbar.
                </Typography>
              </Stack>
            </Paper>

            <Typography variant="caption" color="text.secondary" textAlign="center">
              Steuerung: W/S oder Up/Down, Lenken: A/D oder Left/Right, Drift: Leertaste, Reset: R, Garage: G, Race Start: T
            </Typography>
          </>
        )}

        {isFullscreen && (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10000
            }}
          >
            <Button
              size="small"
              variant={touchControlsEnabled ? "contained" : "outlined"}
              color={touchControlsEnabled ? "success" : "inherit"}
              onClick={onToggleTouchControls}
              sx={{ 
                fontWeight: 600,
                borderColor: touchControlsEnabled ? 'inherit' : '#fff',
                color: touchControlsEnabled ? 'inherit' : '#fff'
              }}
            >
              🎮 Touch {touchControlsEnabled ? "AN" : "AUS"}
            </Button>
            <Button variant="contained" color="primary" onClick={onPauseToggle}>
              {paused ? 'Fortsetzen' : 'Pause'}
            </Button>
            <Button variant="outlined" sx={{ backgroundColor: '#333', color: '#fff' }} onClick={onReset}>
              Reset
            </Button>
            <Button variant="outlined" sx={{ backgroundColor: '#333', color: '#fff' }} onClick={onRaceStart}>
              Race Start
            </Button>
            <Button variant="outlined" sx={{ backgroundColor: '#333', color: '#fff' }} onClick={onToggleFullscreen}>
              Vollbild beenden
            </Button>
          </Stack>
        )}
        
        {/* Mobile Virtual Joystick & Touch Controls (Multi-Touch Support) */}
        {touchControlsEnabled && onJoystickChange && (
          <>
            {/* Virtual Joystick für Lenkung (links) */}
            <VirtualJoystick
              size={120}
              position="left"
              color="#00aaff"
              onChange={onJoystickChange}
            />
            
            {/* Touch Buttons für Gas/Bremse/Drift (rechts) */}
            <Box
              sx={{
                position: 'fixed',
                bottom: 20,
                right: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                zIndex: 9999
              }}
            >
              <TouchButton
                label="GAS"
                onPress={(pressed) => {
                  if (createTouchHandlers) {
                    if (pressed) createTouchHandlers('KeyW').onPointerDown();
                    else createTouchHandlers('KeyW').onPointerUp();
                  }
                }}
                variant="contained"
                color="success"
                sx={{ minWidth: 80, minHeight: 60, fontSize: '1.1rem', fontWeight: 'bold' }}
              />
              <TouchButton
                label="BREMSE"
                onPress={(pressed) => {
                  if (createTouchHandlers) {
                    if (pressed) createTouchHandlers('KeyS').onPointerDown();
                    else createTouchHandlers('KeyS').onPointerUp();
                  }
                }}
                variant="contained"
                color="error"
                sx={{ minWidth: 80, minHeight: 60, fontSize: '1.1rem', fontWeight: 'bold' }}
              />
              <TouchButton
                label="DRIFT"
                onPress={(pressed) => {
                  if (createTouchHandlers) {
                    if (pressed) createTouchHandlers('Space').onPointerDown();
                    else createTouchHandlers('Space').onPointerUp();
                  }
                }}
                variant="contained"
                color="warning"
                sx={{ minWidth: 80, minHeight: 60, fontSize: '1.1rem', fontWeight: 'bold' }}
              />
            </Box>
          </>
        )}
      </Stack>

      <RaceSetupMenu
        open={raceSetupOpen}
        onClose={onCloseRaceSetup}
        onStart={onStartRaceSetup}
        onOpenGarage={onOpenGarageFromSetup}
      />
    </Box>
  );
};

export default CarRaceGameView;
